import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, DollarSign } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Invoice, Payment } from '../../lib/supabase';

interface InvoiceWithPatient extends Invoice {
  patient?: {
    id: string;
    name: string;
    unique_id: string;
    phone: string;
  };
}

export default function DueCollectionPage() {
  const { formatCurrency } = useCurrency();
  const [dueInvoices, setDueInvoices] = useState<InvoiceWithPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithPatient | null>(null);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_method: 'cash' as 'cash' | 'card' | 'bank_transfer' | 'mobile_banking',
    reference_number: '',
    notes: '',
  });

  useEffect(() => {
    loadDueInvoices();
  }, []);

  const loadDueInvoices = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setDueInvoices(getMockDueInvoices());
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          patient:patients(id, name, unique_id, phone)
        `)
        .gt('balance_due', 0)
        .in('status', ['partial', 'overdue', 'sent'])
        .order('due_date', { ascending: true });

      if (error) throw error;
      setDueInvoices(data || []);
    } catch (error) {
      console.error('Error loading due invoices:', error);
      setDueInvoices(getMockDueInvoices());
    } finally {
      setLoading(false);
    }
  };

  const getMockDueInvoices = (): InvoiceWithPatient[] => {
    return [
      {
        id: '1',
        patient_id: '1',
        invoice_number: 'INV-2025-001',
        issue_date: '2025-01-15',
        due_date: '2025-02-15',
        subtotal: 5000,
        discount_amount: 0,
        tax_rate: 0,
        tax_amount: 0,
        total: 5000,
        amount_paid: 2000,
        balance_due: 3000,
        status: 'partial',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        patient: {
          id: '1',
          name: 'John Doe',
          unique_id: 'P-001',
          phone: '01712345678',
        },
      },
      {
        id: '2',
        patient_id: '2',
        invoice_number: 'INV-2025-002',
        issue_date: '2025-01-10',
        due_date: '2025-01-25',
        subtotal: 8000,
        discount_amount: 500,
        tax_rate: 0,
        tax_amount: 0,
        total: 7500,
        amount_paid: 0,
        balance_due: 7500,
        status: 'overdue',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        patient: {
          id: '2',
          name: 'Jane Smith',
          unique_id: 'P-002',
          phone: '01798765432',
        },
      },
      {
        id: '3',
        patient_id: '3',
        invoice_number: 'INV-2025-003',
        issue_date: '2025-01-20',
        due_date: '2025-02-20',
        subtotal: 3500,
        discount_amount: 0,
        tax_rate: 0,
        tax_amount: 0,
        total: 3500,
        amount_paid: 1500,
        balance_due: 2000,
        status: 'partial',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        patient: {
          id: '3',
          name: 'Ahmed Khan',
          unique_id: 'P-003',
          phone: '01856789012',
        },
      },
    ];
  };

  const handleCollectPayment = (invoice: InvoiceWithPatient) => {
    setSelectedInvoice(invoice);
    setPaymentData({
      amount: invoice.balance_due,
      payment_method: 'cash',
      reference_number: '',
      notes: '',
    });
    setShowPaymentModal(true);
  };

  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) return;

    try {
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        alert('Payment recorded successfully (mock mode)');
        setShowPaymentModal(false);
        await loadDueInvoices();
        return;
      }

      const payment: Partial<Payment> = {
        invoice_id: selectedInvoice.id,
        amount: paymentData.amount,
        payment_method: paymentData.payment_method,
        reference_number: paymentData.reference_number || undefined,
        notes: paymentData.notes || undefined,
        payment_date: new Date().toISOString().split('T')[0],
      };

      const { error } = await supabase.from('payments').insert([payment]);
      if (error) throw error;

      alert('Payment recorded successfully');
      setShowPaymentModal(false);
      await loadDueInvoices();
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Failed to record payment');
    }
  };

  const filteredInvoices = dueInvoices.filter(invoice => {
    const matchesSearch = 
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.patient?.unique_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.patient?.phone?.includes(searchTerm);
    return matchesSearch;
  });

  const totalDue = filteredInvoices.reduce((sum, inv) => sum + inv.balance_due, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <Home className="w-4 h-4" />
          <span>/</span>
          <span>Billings/Reception</span>
          <span>/</span>
          <span className="text-gray-900 font-medium">Due Collection</span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-none px-6 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Due Invoices</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{filteredInvoices.length}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Outstanding</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(totalDue)}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue Invoices</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">
                  {filteredInvoices.filter(inv => inv.status === 'overdue').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by invoice number, patient name, ID, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance Due
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      No due invoices found
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invoice.invoice_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{invoice.patient?.name}</div>
                          <div className="text-gray-500 text-xs">{invoice.patient?.unique_id}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invoice.patient?.phone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invoice.due_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(invoice.total)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        {formatCurrency(invoice.amount_paid)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-bold">
                        {formatCurrency(invoice.balance_due)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            invoice.status === 'overdue'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleCollectPayment(invoice)}
                          className="flex items-center space-x-1 px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
                        >
                          <DollarSign className="w-4 h-4" />
                          <span>Collect</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Collect Payment</h2>
              <p className="text-sm text-gray-600 mt-1">
                Invoice: {selectedInvoice.invoice_number} | Patient: {selectedInvoice.patient?.name}
              </p>
            </div>
            <form onSubmit={handleSubmitPayment} className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(selectedInvoice.total)}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Already Paid:</span>
                    <span className="ml-2 font-semibold text-green-600">
                      {formatCurrency(selectedInvoice.amount_paid)}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Balance Due:</span>
                    <span className="ml-2 font-bold text-red-600 text-lg">
                      {formatCurrency(selectedInvoice.balance_due)}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Amount (৳) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  min="0"
                  max={selectedInvoice.balance_due}
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_banking">Mobile Banking</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Number
                </label>
                <input
                  type="text"
                  value={paymentData.reference_number}
                  onChange={(e) => setPaymentData({ ...paymentData, reference_number: e.target.value })}
                  placeholder="Transaction ID, Check number, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
